import { Field, Title, Subtitle, SubmitButton } from '~components'
import { Values, Data } from './interfaces'
import { useNavigate, useStore, useTitle } from '~hooks'
import { object, string } from '~utils/validation'
import { Auth } from '~services'
import { useRequest } from 'ahooks'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import { track } from '@amplitude/analytics-browser'
import { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { verifyIsMobileAndroid } from '~utils/verifyIsMobileAndroid'
import { isDesktop } from '~utils/isDesktop'
import { useHeader } from 'hooks/useHeaderStore'
import { isiOSDevice } from '~utils/isiOSDevice'

function Cpf() {
  // Zendesk status
  const [zd] = useSearchParams()
  const isDesktopDevice = isDesktop()
  const isMobileAndroid = verifyIsMobileAndroid()
  const isiOS = isiOSDevice()
  
  const blockedScreen = `${process.env.REACT_APP_FRETEBRAS_SITE}/bloqueado`
  const { hideHeader, showHeader } = useHeader()

  const store = useStore()
  const defaultValues = {
    cpf: store.cpf || '',
  }

  const schema = object({
    cpf: string().cpf('CPF inválido.'),
  })
  const handleShowValidation = (value: string) => value.length > 13

  const {
    register,
    handleSubmit,
    watch,
    formState: { isValid, errors },
  } = useForm({ defaultValues, resolver: yupResolver(schema), mode: 'onChange' })
  const navigate = useNavigate()
  const cpf = watch('cpf')

  const { executeRecaptcha } = useGoogleReCaptcha()

  const { loading, runAsync } = useRequest<Data, [string, string]>(Auth.cpfRegistered, {
    manual: true,
    onError: () => navigate('/erro'),
  })

  useTitle('Login - Fretebras')

  async function onSubmit(values: Values) {
    const recaptchaToken = await executeRecaptcha('action')
    const data = await runAsync(values.cpf, recaptchaToken)
    if (typeof data === 'string') return

    store.update({ ...values, plate: data.plate })

    if (data.has_user) {
      navigate(`/entrar/senha${zd.get('zd') ? '?zd=1' : ''}`, {
        state: { previousPath: '/entrar' },
      })
    } else if (!data.has_profile && (isDesktopDevice || isiOS)) {
      window.open(blockedScreen, '_self')
    } else if (!data.has_profile && !isDesktopDevice) {
      navigate('/cadastrar', { state: { previousPath: '/entrar' } })
    } else if (!data.has_profile) {
      navigate('/cadastrar', { state: { previousPath: '/entrar' } })
    } else if (!data.plate) {
      navigate('/cadastrar/confirmar-celular', { state: { previousPath: '/entrar' } })
    } else if (data.plate) {
      navigate('/entrar/placa/senha', { state: { previousPath: '/entrar' } })
    }
  }

  const handleUserLockout = (): void => {
    if (isMobileAndroid) {
      window.open(blockedScreen, '_self')
    }
    showHeader()
  }

  useEffect(() => {
    hideHeader()
    track('cadastro_login_cpf')
    handleUserLockout()
  }, [])

  useEffect(() => {
    if (errors.cpf && cpf.length === 14) {
      track('cadastro_login_cpf', { errormessage: 'cpf_invalido' })
    }
  }, [errors, cpf])

  return isMobileAndroid ? null : (
    <form onSubmit={handleSubmit(onSubmit)} aria-labelledby="form-cpf-title">
      <Title id="form-cpf-title">Olá, boas-vindas!</Title>
      <Subtitle>Digite seu CPF.</Subtitle>
      <Field
        register={register}
        defaultValue={defaultValues.cpf}
        name="cpf"
        label="CPF"
        mask="999.999.999-99"
        type="tel"
        error={errors.cpf}
        showSuccess
        isValid={isValid}
        whenShowValidation={handleShowValidation}
      />
      <SubmitButton width="100%" loading={loading} disabled={!isValid}>
        Continuar
      </SubmitButton>
    </form>
  )
}

export default Cpf
